import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Check, Info, ArrowRight, ArrowLeft, Star, Plus } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formulas = [
  {
    id: "start",
    name: "PIXORA Start",
    price: 149,
    description: "Galerie digitale, 20 cadres, accessoires, flash LED",
    features: ["Galerie photos digitale", "20 cadres personnalisables", "Accessoires photo", "Flash LED professionnel", "Installation et reprise incluses"]
  },
  {
    id: "signature",
    name: "PIXORA Signature",
    price: 199,
    description: "50 impressions haute qualité incluses",
    features: ["Tout de Start +", "50 impressions haute qualité"],
    popular: true
  },
  {
    id: "prestige",
    name: "PIXORA Prestige",
    price: 279,
    description: "150 impressions haute qualité",
    features: ["Tout de Signature +", "150 impressions"]
  },
  {
    id: "wedding",
    name: "PIXORA Wedding",
    price: 389,
    description: "350 impressions, livre d'or personnalisé, props mariage",
    features: ["Formule mariage complète", "350 impressions", "Livre d'or personnalisé", "Props premium mariage"],
    highlight: true
  }
];

const optionsAvailable = {
  start: [],
  signature: [
    { id: "impressions_50", name: "50 impressions supplémentaires", price: 39 }
  ],
  prestige: [
    { id: "livre_or", name: "Livre d'or", price: 29 }
  ],
  wedding: []
};

const eventTypes = [
  "Mariage",
  "Anniversaire",
  "Baptême",
  "Soirée privée",
  "Événement corporate",
  "Autre"
];

export default function ReservationPage() {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reservedDates, setReservedDates] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    zone: "nord-isere-est-lyonnais",
    formula: "",
    options: [],
    distance_km: "",
    event_address: "",
    event_postal_code: "",
    event_city: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [totalEstimate, setTotalEstimate] = useState(0);
  
  useEffect(() => {
    const fetchReservedDates = async () => {
      try {
        const response = await axios.get(`${API}/reservations/dates`);
        setReservedDates(response.data.map(d => new Date(d)));
      } catch (error) {
        console.error("Error fetching reserved dates:", error);
      }
    };
    fetchReservedDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    let total = 0;
    const formula = formulas.find(f => f.id === formData.formula);
    if (formula) total += formula.price;
    
    const currentOptions = optionsAvailable[formData.formula] || [];
    formData.options.forEach(optId => {
      const opt = currentOptions.find(o => o.id === optId);
      if (opt) total += opt.price;
    });
    
    const km = parseInt(formData.distance_km) || 0;
    if (km > 20) total += (km - 20) * 0.50 * 4;
    
    setTotalEstimate(total);
  }, [formData]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({
        ...prev,
        options: checked ? [...prev.options, value] : prev.options.filter(o => o !== value)
      }));
    } else {
      if (name === "formula") {
        setFormData(prev => ({ ...prev, [name]: value, options: [] }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    }
  };
  
  const handleDateSelect = (date) => setSelectedDate(date);
  
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, addDays(today, 7))) return true;
    return reservedDates.some(d => d.toDateString() === date.toDateString());
  };
  
  const nextStep = () => {
    if (step === 1 && !selectedDate) { toast.error("Veuillez sélectionner une date"); return; }
    if (step === 2 && !formData.formula) { toast.error("Veuillez sélectionner une formule"); return; }
    setStep(prev => prev + 1);
  };
  
  const prevStep = () => setStep(prev => prev - 1);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        ...formData,
        event_date: format(selectedDate, "yyyy-MM-dd"),
        distance_km: parseInt(formData.distance_km) || 0
      });
      setSubmitted(true);
      toast.success("Réservation envoyée avec succès !");
    } catch (error) {
      toast.error("Erreur lors de la réservation. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };
  
  const currentOptions = optionsAvailable[formData.formula] || [];
  
  if (submitted) {
    return (
      <div className="pt-24 md:pt-32 lg:pt-36 min-h-screen flex items-center justify-center" data-testid="reservation-success">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A88B4A] flex items-center justify-center">
            <Check size={40} weight="bold" className="text-black" />
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-4">Réservation envoyée !</h2>
          <p className="text-[#B8A88A] mb-4">
            Merci pour votre réservation du <span className="text-white font-semibold">{format(selectedDate, "d MMMM yyyy", { locale: fr })}</span>
          </p>
          <div className="glass rounded-xl p-4 mb-8 border border-[#C9A962]/30">
            <p className="text-sm text-[#B8A88A]">Total estimé</p>
            <p className="text-3xl font-extrabold gradient-text">{totalEstimate.toFixed(2)}€</p>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="pt-24 md:pt-32 lg:pt-36" data-testid="reservation-page">
      <Seo
        title="Réserver un Photobooth à Bourgoin-Jallieu | Pixora"
        description="Réservez votre photobooth Pixora en ligne pour un événement à Bourgoin-Jallieu ou en Nord-Isère. Formules dès 149€, calendrier en temps réel."
        path="/reservation"
      />
      <section className="py-16 md:py-24 bokeh-section min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CalendarCheck size={20} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#B8A88A]">Réservation</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6">
              Réservez votre <span className="gradient-text">Date</span>
            </h1>
            
            {/* Progress */}
            <div className="flex items-center justify-center gap-4 mt-8">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s ? "bg-gradient-to-br from-[#C9A962] to-[#A88B4A] text-black" : "bg-[#0A0A0A] text-[#8A7A5A] border border-[#C9A962]/30"
                  }`}>{s}</div>
                  {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-[#C9A962]" : "bg-[#C9A962]/20"}`} />}
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="card bg-[#0A0A0A] rounded-2xl p-8 border border-[#C9A962]/20">
            
            {/* Step 1: Date */}
            {step === 1 && (
              <div data-testid="step-1">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">Choisissez votre date</h3>
                <div className="flex justify-center mb-8">
                  <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} disabled={isDateDisabled} locale={fr} className="rounded-xl border border-[#C9A962]/30 bg-[#000000] p-4" />
                </div>
                {selectedDate && (
                  <p className="text-center text-[#B8A88A] mb-6">
                    Date sélectionnée : <span className="text-white font-semibold">{format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}</span>
                  </p>
                )}
                <div className="glass rounded-xl p-4 border border-[#C9A962]/30">
                  <div className="flex items-start gap-3">
                    <Info size={20} className="text-[#C9A962] mt-0.5" />
                    <p className="text-[#B8A88A] text-sm">Réservations au moins 7 jours à l'avance.</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Formula + Options */}
            {step === 2 && (
              <div data-testid="step-2">
                <h3 className="text-2xl font-bold text-white mb-6">Choisissez votre formule</h3>
                
                {/* Formulas Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {formulas.map((formula) => (
                    <label
                      key={formula.id}
                      className={`relative block rounded-xl border cursor-pointer transition-all p-4 ${
                        formData.formula === formula.id
                          ? "border-[#C9A962] bg-[#C9A962]/10"
                          : formula.popular
                            ? "border-[#C9A962]/50 hover:border-[#C9A962]"
                            : "border-[#C9A962]/20 hover:border-[#C9A962]/50"
                      }`}
                      data-testid={`formula-${formula.id}`}
                    >
                      <input type="radio" name="formula" value={formula.id} checked={formData.formula === formula.id} onChange={handleChange} className="hidden" />
                      
                      {formula.popular && (
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <span className="bg-[#C9A962] text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Star size={10} weight="fill" /> Populaire
                          </span>
                        </div>
                      )}
                      {formula.highlight && (
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <span className="bg-[#C9A962] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">💍 Mariage</span>
                        </div>
                      )}
                      
                      <div className="text-center pt-2">
                        <p className="text-white font-semibold text-sm">{formula.name}</p>
                        <p className="text-2xl font-bold gradient-text my-2">{formula.price}€</p>
                        <p className="text-[#8A7A5A] text-xs">{formula.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                
                {/* Options avec checkbox */}
                {currentOptions.length > 0 && (
                  <div className="mb-8 p-5 rounded-xl bg-[#000000] border border-[#C9A962]/30">
                    <p className="text-[#C9A962] font-semibold mb-4 flex items-center gap-2">
                      <Plus size={16} weight="bold" /> Options disponibles
                    </p>
                    <div className="space-y-3">
                      {currentOptions.map((option) => (
                        <label key={option.id} className="flex items-center justify-between cursor-pointer p-3 rounded-lg hover:bg-[#C9A962]/5 transition-colors" data-testid={`option-${option.id}`}>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              value={option.id}
                              checked={formData.options.includes(option.id)}
                              onChange={handleChange}
                              className="w-5 h-5 rounded border-[#C9A962]/30 bg-[#0A0A0A] text-[#C9A962] focus:ring-[#C9A962] focus:ring-offset-0"
                            />
                            <span className="text-white">{option.name}</span>
                          </div>
                          <span className="text-[#C9A962] font-bold">+{option.price}€</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Distance */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">Distance de Bourgoin-Jallieu (km)</label>
                  <input type="number" name="distance_km" value={formData.distance_km} onChange={handleChange} className="input-field" placeholder="ex: 15" min="0" data-testid="distance-input" />
                  <p className="text-[#8A7A5A] text-xs mt-2">Inclus jusqu'à 20 km. Au-delà : 0,50€/km (x4 trajets)</p>
                </div>
                
                {/* Récap rapide */}
                {formData.formula && (
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#C9A962]/10 to-transparent border border-[#C9A962]/30">
                    <div className="flex justify-between items-center">
                      <span className="text-[#B8A88A]">Total estimé</span>
                      <span className="text-2xl font-bold gradient-text">{totalEstimate.toFixed(2)}€</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Step 3: Contact */}
            {step === 3 && (
              <form onSubmit={handleSubmit} data-testid="step-3">
                <h3 className="text-2xl font-bold text-white mb-6">Vos coordonnées</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-[#B8A88A] mb-2">Nom complet *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-field" placeholder="Votre nom" data-testid="name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B8A88A] mb-2">Téléphone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="input-field" placeholder="06 00 00 00 00" data-testid="phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B8A88A] mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-field" placeholder="votre@email.com" data-testid="email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#B8A88A] mb-2">Type d'événement *</label>
                    <select name="event_type" value={formData.event_type} onChange={handleChange} required className="input-field" data-testid="event-type-select">
                      <option value="">Sélectionnez...</option>
                      {eventTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm uppercase tracking-[0.15em] text-[#C9A962] font-semibold mb-3">Adresse de l'événement</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[#B8A88A] mb-2">Adresse *</label>
                      <input type="text" name="event_address" value={formData.event_address} onChange={handleChange} required className="input-field" placeholder="ex: 12 rue des Lilas" data-testid="event-address-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B8A88A] mb-2">Code postal *</label>
                      <input type="text" name="event_postal_code" value={formData.event_postal_code} onChange={handleChange} required className="input-field" placeholder="38300" pattern="[0-9]{5}" maxLength="5" inputMode="numeric" data-testid="event-postal-code-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B8A88A] mb-2">Ville *</label>
                      <input type="text" name="event_city" value={formData.event_city} onChange={handleChange} required className="input-field" placeholder="Bourgoin-Jallieu" data-testid="event-city-input" />
                    </div>
                  </div>
                </div>
                
                <div className="mb-8">
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">Message (optionnel)</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows={3} className="input-field resize-none" placeholder="Détails supplémentaires..." data-testid="message-input" />
                </div>
                
                {/* Récap final */}
                <div className="p-6 rounded-xl bg-[#000000] border border-[#C9A962]/30 mb-8">
                  <h4 className="text-white font-semibold mb-4 text-lg">Récapitulatif</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-[#C9A962]/10">
                      <span className="text-[#B8A88A]">Date</span>
                      <span className="text-white font-medium">{format(selectedDate, "d MMMM yyyy", { locale: fr })}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#C9A962]/10">
                      <span className="text-[#B8A88A]">Formule</span>
                      <span className="text-white font-medium">{formulas.find(f => f.id === formData.formula)?.name}</span>
                    </div>
                    {(formData.event_address || formData.event_city) && (
                      <div className="flex justify-between gap-4 py-2 border-b border-[#C9A962]/10">
                        <span className="text-[#B8A88A] shrink-0">Adresse</span>
                        <span className="text-white font-medium text-right">
                          {formData.event_address}
                          {formData.event_postal_code || formData.event_city ? <>, <br className="sm:hidden" />{formData.event_postal_code} {formData.event_city}</> : null}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b border-[#C9A962]/10">
                      <span className="text-[#B8A88A]">Prix formule</span>
                      <span className="text-white font-medium">{formulas.find(f => f.id === formData.formula)?.price}€</span>
                    </div>
                    
                    {formData.options.map(optId => {
                      const opt = currentOptions.find(o => o.id === optId);
                      return opt ? (
                        <div key={optId} className="flex justify-between py-2 border-b border-[#C9A962]/10">
                          <span className="text-[#B8A88A]">{opt.name}</span>
                          <span className="text-white font-medium">+{opt.price}€</span>
                        </div>
                      ) : null;
                    })}
                    
                    {parseInt(formData.distance_km) > 20 && (
                      <div className="flex justify-between py-2 border-b border-[#C9A962]/10">
                        <span className="text-[#B8A88A]">Frais km ({formData.distance_km} km)</span>
                        <span className="text-white font-medium">+{((parseInt(formData.distance_km) - 20) * 0.50 * 4).toFixed(2)}€</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between py-3 mt-2">
                      <span className="text-white font-bold text-lg">TOTAL</span>
                      <span className="text-2xl font-extrabold gradient-text">{totalEstimate.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
                
                <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-lg" data-testid="submit-reservation-btn">
                  {loading ? "Envoi en cours..." : "Confirmer la réservation"}
                </button>
              </form>
            )}
            
            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#C9A962]/20">
              {step > 1 ? (
                <button type="button" onClick={prevStep} className="btn-secondary flex items-center gap-2" data-testid="prev-step-btn">
                  <ArrowLeft size={18} /> Précédent
                </button>
              ) : <div />}
              {step < 3 && (
                <button type="button" onClick={nextStep} className="btn-primary flex items-center gap-2" data-testid="next-step-btn">
                  Suivant <ArrowRight size={18} />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
